import React, { useCallback, useMemo } from "react";
import { useForm, useFormState } from "react-hook-form";
import { omit } from "lodash";

import {
  Field,
  FieldLabel,
  FormCard,
  FormPanel,
  LabelRow,
  ToneSegments,
  TwoColumnRow,
} from "../../../../components/FormPanel";
import {
  FormInput,
  FormMoneyInput,
  InputDate,
  SelectForm,
  SelectOptionTypes,
} from "../../../../components/Input";
import {
  buildDraftDetails,
  ConfirmDetail,
  joinDetailParts,
} from "../../../../components/PopUp/confirmShared";
import { DateFormat, formatCurrency, formatDate } from "../../../../utils";
import { useAccountOptions } from "../../../../hooks/useAccountOptions";
import { TransactionTypeOptions } from "../../../Transactions";
import { MobileProgressForm } from "./MobileProgressForm";
import { ProgressPanelProps, ProgressType } from "./types";
import {
  AMOUNT_RULES,
  DEFAULT_PROGRESS,
  FORM_ID,
  SEGMENT_LABELS,
  TONE_BY_TYPE,
  TYPE_OPTIONS,
  accountTypeToStringMap,
} from "./constants";

const DISCARD_PILL_VARIANT_BY_TYPE: Record<string, string> = {
  [TransactionTypeOptions.TRANSFER_IN]: "INCOME",
  [TransactionTypeOptions.TRANSFER_OUT]: "EXPENSE",
  [TransactionTypeOptions.MARKET_UPDATE]: "DEFAULT",
};

const useTransactionForm = (
  account: SelectOptionTypes | null,
  progress?: ProgressType,
) => {
  const form = useForm<ProgressType>({
    defaultValues: progress ?? DEFAULT_PROGRESS,
    mode: "onBlur",
    reValidateMode: "onChange",
  });

  const { control, setValue, watch } = form;
  const { isSubmitting } = useFormState({ control });

  const transactionType = watch("transactionType");
  const prevTransactionType = React.useRef(transactionType?.value);

  React.useEffect(() => {
    if (!transactionType) {
      return;
    }

    if (transactionType.value !== prevTransactionType.current) {
      if (transactionType.value === TransactionTypeOptions.TRANSFER_IN) {
        setValue("toAccount", account);
      } else if (
        transactionType.value === TransactionTypeOptions.TRANSFER_OUT
      ) {
        setValue("fromAccount", account);
      } else {
        setValue("toAccount", null);
        setValue("fromAccount", null);
      }
      prevTransactionType.current = transactionType.value;
    }
  }, [account, setValue, transactionType]);

  return {
    form,
    isSubmitting,
    transactionType,
  };
};

export const ProgressPanel = ({
  title,
  progress,
  account,
  isOpen,
  onClose,
  handleOnSubmit,
}: ProgressPanelProps) => {
  const accountId = account ? Number(account.value) : null;
  const shouldShowOnAccount = !account;
  const { accountOptions, load } = useAccountOptions({
    excludeId: accountId ?? undefined,
    investmentsOnly: shouldShowOnAccount,
  });

  const transactionTypeMapped =
    (progress &&
      TYPE_OPTIONS.find(
        (type) =>
          type.value === accountTypeToStringMap[progress.transactionType],
      )) ||
    TYPE_OPTIONS[0];
  const progressDefault: ProgressType | undefined = progress
    ? {
        ...omit(progress, [
          "transactionType",
          "fromAccountId",
          "fromAccountName",
          "toAccountId",
          "toAccountName",
        ]),
        transactionType: transactionTypeMapped,
        toAccount: progress.toAccountName
          ? {
              label: progress.toAccountName,
              value: String(progress.toAccountId),
            }
          : null,
        fromAccount: progress.fromAccountName
          ? {
              label: progress.fromAccountName,
              value: String(progress.fromAccountId),
            }
          : null,
      }
    : undefined;

  const { form, isSubmitting, transactionType } = useTransactionForm(
    account,
    progressDefault,
  );
  const { control, setValue, register, clearErrors, getValues } = form;

  const handleTypeChange = useCallback(
    (option: SelectOptionTypes) => {
      setValue("transactionType", option, {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [setValue],
  );

  const validationRules = useMemo(
    () => ({
      amount: AMOUNT_RULES,
      transactionType: {
        required: "Transaction type is required",
      },
      fromAccount: {
        required: "From account is required",
        min: { value: 1, message: "From account is required" },
      },
      toAccount: {
        required: "To account is required",
        min: { value: 1, message: "To account is required" },
      },
      transactionDate: {
        required: "Date is required",
      },
      note: {
        minLength: {
          value: 3,
          message: "Description must be at least 3 characters",
        },
        maxLength: {
          value: 255,
          message: "Description cannot exceed 255 characters",
        },
      },
    }),
    [],
  );

  const shouldShowFromAccount =
    transactionType.value !== TransactionTypeOptions.TRANSFER_OUT &&
    transactionType.value !== TransactionTypeOptions.MARKET_UPDATE;
  const shouldShowToAccount =
    transactionType.value !== TransactionTypeOptions.TRANSFER_IN &&
    transactionType.value !== TransactionTypeOptions.MARKET_UPDATE;
  const shouldShowNote =
    transactionType.value !== TransactionTypeOptions.MARKET_UPDATE;
  const showAccountCard =
    shouldShowFromAccount ||
    shouldShowToAccount ||
    shouldShowOnAccount ||
    shouldShowNote;

  const isEditProgress = Boolean(progress);

  const buildDiscardDetails = (): ConfirmDetail => {
    const formValues = getValues();
    const typeValue = transactionType.value;
    const isOutflow = typeValue === TransactionTypeOptions.TRANSFER_OUT;
    const amountNumber = Number.parseFloat(formValues.amount);
    const hasAmount = !Number.isNaN(amountNumber) && amountNumber > 0;
    const signedAmount = isOutflow ? -amountNumber : amountNumber;
    const formattedAmount = hasAmount
      ? formatCurrency(signedAmount)
      : undefined;

    if (isEditProgress) {
      return {
        pill: {
          label: transactionType.label.toUpperCase(),
          variant: DISCARD_PILL_VARIANT_BY_TYPE[typeValue] ?? "DEFAULT",
        },
        title: transactionType.label,
        subtitle: joinDetailParts(
          formValues.note,
          formatDate(formValues.transactionDate, DateFormat.DMY_SPACE),
        ),
        amount: formattedAmount,
        amountVariant: TONE_BY_TYPE[typeValue] ?? "neutral",
      };
    }

    return buildDraftDetails({
      noun: "progress",
      fieldValues: [
        formValues.amount,
        formValues.fromAccount,
        formValues.toAccount,
        formValues.transactionDate,
        formValues.note,
      ],
      amount: formattedAmount,
      primaryLabel: transactionType.label,
    });
  };

  return (
    <FormPanel
      open={isOpen}
      onClose={onClose}
      ariaLabel="Log investment contribution"
      eyebrow="INVESTMENT PROGRESS"
      title={title}
      form={form}
      resetValues={progressDefault ?? DEFAULT_PROGRESS}
      formId={FORM_ID}
      onSubmit={handleOnSubmit}
      submitLabel="Save"
      discard={{
        title: "Discard your changes?",
        details: buildDiscardDetails,
      }}
      load={load}
      mobile={{
        title,
        submitLabel: "Save",
        fields: (
          <MobileProgressForm
            control={control}
            register={register}
            setValue={setValue}
            clearErrors={clearErrors}
            transactionType={transactionType}
            accountOptions={accountOptions}
            shouldShowFromAccount={shouldShowFromAccount}
            shouldShowToAccount={shouldShowToAccount}
            shouldShowOnAccount={shouldShowOnAccount}
            shouldShowNote={shouldShowNote}
            isTypeDisabled={shouldShowOnAccount}
            isSubmitting={isSubmitting}
            onTypeChange={handleTypeChange}
          />
        ),
      }}
    >
      <FormCard>
        <Field>
          <LabelRow>
            <FieldLabel>Type *</FieldLabel>
          </LabelRow>
          <ToneSegments
            groupLabel="Type"
            options={TYPE_OPTIONS}
            toneByValue={TONE_BY_TYPE}
            labelByValue={SEGMENT_LABELS}
            selectedValue={transactionType.value}
            disabled={isSubmitting || shouldShowOnAccount}
            onChange={handleTypeChange}
          />
        </Field>

        <TwoColumnRow>
          <Field>
            <LabelRow>
              <FieldLabel htmlFor="inv-progress-date">Date *</FieldLabel>
            </LabelRow>
            <InputDate
              id="inv-progress-date"
              rules={validationRules.transactionDate}
              name="transactionDate"
              control={control}
              placeholder="YYYY/MM/DD"
              maxDate={new Date()}
              disabled={isSubmitting}
            />
          </Field>

          <Field>
            <LabelRow>
              <FieldLabel htmlFor="inv-progress-amount">Amount *</FieldLabel>
            </LabelRow>
            <FormMoneyInput
              id="inv-progress-amount"
              required
              rules={validationRules.amount}
              name="amount"
              control={control}
              disabled={isSubmitting}
              placeholder="Enter Amount"
              autoFocus={true}
            />
          </Field>
        </TwoColumnRow>
      </FormCard>

      {showAccountCard && (
        <FormCard>
          {shouldShowFromAccount && (
            <Field>
              <LabelRow>
                <FieldLabel htmlFor="inv-progress-from-account">
                  From account *
                </FieldLabel>
              </LabelRow>
              <SelectForm
                id="inv-progress-from-account"
                key="fromAccount"
                rules={validationRules.fromAccount}
                size="small"
                name="fromAccount"
                control={control}
                defaultValue={progressDefault?.fromAccount ?? null}
                options={accountOptions}
                disabled={isSubmitting}
              />
            </Field>
          )}

          {shouldShowToAccount && (
            <Field>
              <LabelRow>
                <FieldLabel htmlFor="inv-progress-to-account">
                  To account *
                </FieldLabel>
              </LabelRow>
              <SelectForm
                id="inv-progress-to-account"
                key="toAccount"
                rules={validationRules.toAccount}
                size="small"
                name="toAccount"
                control={control}
                defaultValue={progressDefault?.toAccount ?? null}
                options={accountOptions}
                disabled={isSubmitting}
              />
            </Field>
          )}

          {shouldShowOnAccount && (
            <Field>
              <LabelRow>
                <FieldLabel htmlFor="inv-progress-on-account">
                  On account *
                </FieldLabel>
              </LabelRow>
              <SelectForm
                id="inv-progress-on-account"
                key="onAccount"
                rules={validationRules.toAccount}
                size="small"
                name="toAccount"
                control={control}
                defaultValue={progressDefault?.toAccount ?? null}
                options={accountOptions}
                disabled={isSubmitting}
              />
            </Field>
          )}

          {shouldShowNote && (
            <Field>
              <LabelRow>
                <FieldLabel htmlFor="inv-progress-note">Description</FieldLabel>
              </LabelRow>
              <FormInput
                id="inv-progress-note"
                rules={validationRules.note}
                name="note"
                control={control}
                handleClear={false}
                disabled={isSubmitting}
                placeholder="Enter a description"
              />
            </Field>
          )}
        </FormCard>
      )}
    </FormPanel>
  );
};
